import { UploadFileInput, StyledCustomDialog, ImageFullScreenViewer, UploadAndPreviewImageComponent, UploadAndPreviewImageTitle } from "@iventis/components";
import { Asset } from "@iventis/domain-model/model/asset";
import { MapObjectImagePayload } from "@iventis/domain-model/model/mapObjectImagePayload";
import { StyledFieldLabelLight } from "@iventis/styles";
import { toast } from "@iventis/toasts/src/toast";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { AllowedMapObjectImagePayloads } from "@iventis/types";
import { fileToBase64, getAssetSignature } from "@iventis/utilities";
import { DialogTitle } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState, FunctionComponent, useRef } from "react";

const MAX_FILE_SIZE_MB = 15;

interface ObjectDetailsDataFieldImageProps {
    /** Name of the datafield */
    name: string;
    /** DataFieldValue which can be an asset id or the MapObjectImagePayload object */
    dataFieldValue: string | MapObjectImagePayload;
    /** Async function which gets the image */
    getImage: (id: string) => Promise<Asset>;
    /** Called when the user changes either the name or the image of the datafield */
    valueChanged: (value: AllowedMapObjectImagePayloads) => void;
}

export const ObjectDetailsDataFieldImage: FunctionComponent<ObjectDetailsDataFieldImageProps> = ({ name, dataFieldValue, valueChanged, getImage }) => {
    const translate = useIventisTranslate();
    const [uploadFileModal, setUploadFileModal] = useState<boolean>(false);
    const [showImage, setShowImage] = useState<boolean>(false);

    /** Holds a local version of the image and name while the api is saving */
    const localImage = useRef<MapObjectImagePayload | null>(null);

    // Get QueryClient from the context
    const queryClient = useQueryClient();

    // Either fetch the image with get image or use the dataFieldValue if it's already an image
    const { data: image } = useQuery<MapObjectImagePayload>(
        ["map-object-image", dataFieldValue],
        async () => {
            // Need this check for type safety, we know it is a string due to enabled check
            if (typeof dataFieldValue !== "string") {
                return null;
            }
            const imageAsset = await getImage(dataFieldValue);
            return { imageData: getAssetSignature(imageAsset?.assetUrl, imageAsset?.authoritySignature), imageName: imageAsset.name };
        },
        {
            // Only get the image when the dataFieldValue is a string
            enabled: typeof dataFieldValue === "string",
        }
    );

    const imageAdded = localImage?.current != null || image != null;
    const handleFileDelete = () => {
        valueChanged(null);
        localImage.current = null;
        queryClient.invalidateQueries({ queryKey: ["map-object-image"] });
    };

    return (
        <>
            <StyledFieldLabelLight className="object-data-field-label">{name}</StyledFieldLabelLight>
            <UploadFileInput
                onInputClick={() => (dataFieldValue == null ? setUploadFileModal(true) : setShowImage(true))}
                onButtonClick={() => (imageAdded ? handleFileDelete() : setUploadFileModal(true))}
                buttonText={!imageAdded ? translate(Content.map6.fileUpload.choose_file) : translate(Content.common.delete)}
                classNames={{ input: "file-upload-input", button: "file-upload-button", container: "file-upload-container" }}
                fileName={localImage?.current?.imageName ?? image?.imageName ?? translate(Content.map7.attributes.noImageSelected)}
                containerDataTestId={`attribute-value-${name}`}
                fileAdded={imageAdded}
                dataTestId={imageAdded ? "delete-file-button" : "select-file-button"}
            />
            <StyledCustomDialog open={uploadFileModal} $overflow="hidden" maxWidth="lg" onClose={() => setUploadFileModal(false)}>
                <DialogTitle>
                    <UploadAndPreviewImageTitle title={translate(Content.map7.attributes.imageTitle)} />
                </DialogTitle>
                <UploadAndPreviewImageComponent
                    imageUrl={localImage.current?.imageData ?? image?.imageData}
                    onFileUpload={async (file) => {
                        const { withHeader: image } = await fileToBase64(file);
                        localImage.current = { imageData: image, imageName: file.name };
                        valueChanged({ imageData: image, imageName: file.name });
                    }}
                    onClose={() => setUploadFileModal(false)}
                    imageName={localImage.current?.imageName ?? image?.imageName}
                    topText={translate(Content.map7.attributes.imageDescription)}
                    maxFileSize={MAX_FILE_SIZE_MB}
                    onTooLargeFile={() => {
                        toast.warning({
                            title: translate(Content.common.warning),
                            message: translate(Content.map7.importGeoJson.fileTooLarge.message, { size: `${MAX_FILE_SIZE_MB} Mb` }),
                        });
                    }}
                />
            </StyledCustomDialog>
            <StyledCustomDialog $overflow="hidden" maxWidth="md" open={showImage} onClose={() => setShowImage(false)}>
                <ImageFullScreenViewer
                    imageUrl={image?.imageData ?? localImage?.current?.imageData}
                    name={localImage?.current?.imageName ?? image?.imageName}
                    onNameChange={(name) => {
                        localImage.current = { imageData: image?.imageData, imageName: name };
                        valueChanged({ imageName: name });
                    }}
                    onClose={() => setShowImage(false)}
                    dataTestId="object-details-image-viewer"
                />
            </StyledCustomDialog>
        </>
    );
};
